// @flow

import React, { Component, type Node } from 'react';
import BlockAndUnfollow from 'app/common/react/components/undiscovery/BlockAndUnfollow/BlockAndUnfollow';
import cx from 'classnames';
import FlagOptions from 'app/common/react/components/undiscovery/FlagOptions';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import templateConst from 'app/common/lib/templateConst';
import type { CommentUser } from 'app/packages/pinterest-flow-types/user';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { Box, Button, Heading, Text } from 'gestalt';
import { Flow, Status } from 'app/common/react/components/undiscovery/UndiscoveryEnums';

const ABOUT_ACCEPTABLE_USE_URL = templateConst.settings.ABOUT_ACCEPTABLE_USE;

const STYLES = {
  contentContainer: {
    color: '#999',
    display: 'table',
    fontSize: 13,
    height: 360,
    width: 550,
  },
  contents: {
    display: 'table-cell',
    padding: '0 18px',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
};

type Props = {|
  aggregated_rich_data_type?: string,
  board?: {
    id: string,
    followed_by_me?: boolean,
    collaborated_by_me?: boolean,
  },
  className?: string,
  flagOptionsConfig: {
    __default: Array<{
      text: string,
      value: string,
      category?: string,
      isTerminalReason?: boolean,
      condition?: boolean,
    }>,
  },
  flagReason?: string,
  flagType: string,
  selectedReason?: string,
  detailedReasons?: Array<string>,
  flow: string,
  onClose: Function,
  onFlowStatusChange: Function,
  onNext?: Function,
  onRevert?: Function,
  onSelectReason: Function,
  onCompletedReport?: Function,
  onCompletedBlock?: Function,
  status: string,
  submitButton: Node,
  title?: string,
  content?: Node,
  showPrimaryAction?: boolean,
  pin?: {
    rich_metadata: Object,
  },
  users?: Array<CommentUser>,
|};

// The base component for flag (reporting) flows. It should be contained within
// other Flag* components, such as <FlagConversation> or <FlagPin>.
// It does not maintain state for `Flow` or `Status`; that should be maintained
// in the parent component, and modified with Flag.handleFlowStatusChange()
export default class Flag extends Component<Props> {
  UNSAFE_componentWillUpdate(nextProps: Props) {
    const { flow, onCompletedReport, onCompletedBlock } = this.props;

    // Change in flow
    if (nextProps.flow !== flow) {
      switch (flow) {
        case Flow.REPORT:
          if (onCompletedReport) {
            onCompletedReport();
          }
          break;
        case Flow.BLOCK:
          if (onCompletedBlock) {
            onCompletedBlock();
          }
          break;
        default:
          break;
      }
    }
  }

  flagOptions = new FlagOptions(this.props.flagOptionsConfig);

  // Close the entire "Flag" flow
  handleClose = () => {
    this.props.onClose();
  };

  // Available in multi-step flows, after the first reason is selected
  handleRevert = () => {
    if (this.props.onRevert) {
      this.props.onRevert();
    }
  };

  // sets the selectedReason as the flagReason
  handleNext = () => {
    if (this.props.onNext) {
      this.props.onNext();
    }
  };

  // Set the `flow` and `status` states on the parent.
  // If either flow or status keys are not set on the argument, then pass-through
  // the current values (provided via props).
  handleFlowStatusChange = (
    { flow = this.props.flow, status = this.props.status }: { flow: string, status: string },
    cb: Function = () => {},
  ) => {
    this.props.onFlowStatusChange({ flow, status }, cb);
  };

  // Select a policy link based on the flag reason
  // For example if the flag reason is 'spam' a link to the spam policy link will be shown.
  renderPolicyLink = () => {
    const { flow, flagReason } = this.props;

    if (flow === Flow.REPORT) {
      if (flagReason === 'attacks') {
        return (
          <p>
            <Text size="xs">
              {interpolateNamedTemplate(
                i18n._(
                  'Learn more about our {{ linkToTopic }}',
                  'footer link to learn more about community guidelines',
                ),
                {
                  linkToTopic: (
                    <NavigateLink key="policyLink" to={ABOUT_ACCEPTABLE_USE_URL}>
                      <Text inline color="red" size="xs">
                        {i18n._(
                          'Community Guidelines',
                          'name of footer link to community guidelines',
                        )}
                      </Text>
                    </NavigateLink>
                  ),
                },
              )}
            </Text>
          </p>
        );
      } else if (flagReason === 'policies') {
        return (
          <p>
            <Text size="xs">
              {interpolateNamedTemplate(
                i18n._('Learn about {{ linkToTopic }}', 'footer link to learn more about a topic'),
                {
                  linkToTopic: (
                    <NavigateLink
                      key="policyLink"
                      to={ABOUT_ACCEPTABLE_USE_URL + '?collapsible-element=0'}
                    >
                      <Text inline color="red" size="xs">
                        {i18n._('our nudity policies', 'name of footer link to policy')}
                      </Text>
                    </NavigateLink>
                  ),
                },
              )}
            </Text>
            <Text size="xs">
              {interpolateNamedTemplate(
                i18n._('Learn about {{ linkToTopic }}', 'footer link to learn more about a topic'),
                {
                  linkToTopic: (
                    <NavigateLink
                      key="policyLink"
                      to={ABOUT_ACCEPTABLE_USE_URL + '?collapsible-element=1'}
                    >
                      <Text inline color="red" size="xs">
                        {i18n._('our policies on hurtful content', 'name of footer link to policy')}
                      </Text>
                    </NavigateLink>
                  ),
                },
              )}
            </Text>
            <Text size="xs">
              {interpolateNamedTemplate(
                i18n._('Learn about {{ linkToTopic }}', 'footer link to learn more about a topic'),
                {
                  linkToTopic: (
                    <NavigateLink
                      key="policyLink"
                      to={ABOUT_ACCEPTABLE_USE_URL + '?collapsible-element=5'}
                    >
                      <Text inline color="red" size="xs">
                        {i18n._('our policies on gore', 'name of footer link to policy')}
                      </Text>
                    </NavigateLink>
                  ),
                },
              )}
            </Text>
          </p>
        );
      }

      if (flagReason === 'spam' || typeof flagReason === 'undefined') {
        return (
          <p className="helpText">
            <Text size="xs">
              {interpolateNamedTemplate(
                i18n._('Learn about {{ linkToTopic }}', 'footer link to learn more about a topic'),
                {
                  linkToTopic: (
                    <NavigateLink key="policyLink" to={ABOUT_ACCEPTABLE_USE_URL}>
                      {flagReason === 'spam'
                        ? i18n._('our spam policies', 'name of footer link to policy')
                        : i18n._('our policies', 'name of footer link to policy')}
                    </NavigateLink>
                  ),
                },
              )}
            </Text>
          </p>
        );
      }
    }
    return undefined;
  };

  // If content is passed in from the Flag* component, allow it to override logic here.
  renderContent = () => {
    const { content, flow, onSelectReason, selectedReason } = this.props;

    if (content) {
      return content;
    } else if (flow === Flow.REPORT) {
      return this.flagOptions.renderFlagLabels({
        flagReason: this.props.flagReason || '__default',
        onSelectReason,
        selectedReason,
      });
    } else if (flow === Flow.BLOCK) {
      // Content defined in <BlockAndUnfollow />
    } else if (flow === Flow.END) {
      return (
        <span style={STYLES.contents}>
          {i18n._(
            'We’ll use your feedback to make your Pinterest experience better.',
            'final message in reporting flow',
          )}
        </span>
      );
    }
    return undefined;
  };

  render() {
    const {
      aggregated_rich_data_type,
      className,
      flagType,
      flow,
      status,
      pin,
      selectedReason,
      detailedReasons,
      submitButton,
      flagReason,
      showPrimaryAction,
      title,
      users,
      board,
    } = this.props;

    let aggregatedRichDataType = aggregated_rich_data_type;

    if (!aggregatedRichDataType && pin && pin.rich_metadata) {
      const rich_metadata = pin.rich_metadata;

      ['recipe', 'tutorial'].forEach((aggregatedType) => {
        if (
          rich_metadata[aggregatedType] &&
          rich_metadata[aggregatedType].from_aggregated_data &&
          rich_metadata[aggregatedType].additional_data.url
        ) {
          aggregatedRichDataType = aggregatedType;
        }
      });
    }

    // Form footer: secondary action
    let secondaryAction = null;
    if (flow === Flow.REPORT) {
      secondaryAction =
        typeof flagReason === 'undefined' ? (
          <Button onClick={this.handleClose} text={i18n._('Cancel', 'cancel form')} />
        ) : (
          <Button onClick={this.handleRevert} text={i18n._('Back', 'previous step in a form')} />
        );
    }

    // Form footer: primary action
    let primaryAction;
    if (flow === Flow.REPORT) {
      if (
        Status.PROCESSING === status ||
        (this.flagOptions.getFlagOption(selectedReason) &&
          this.flagOptions.getFlagOption(selectedReason).isTerminalReason)
      ) {
        primaryAction = submitButton;
      } else if (showPrimaryAction) {
        primaryAction = (
          <Button
            color="red"
            disabled={typeof selectedReason === 'undefined'}
            onClick={this.handleNext}
            text={i18n._('Next', 'go to the next view in the report flow')}
          />
        );
      }
    } else if (flow === Flow.BLOCK) {
      // Primary actions defined in <BlockAndUnfollow>
    } else if (flow === Flow.END) {
      primaryAction = (
        <Button
          color="red"
          onClick={this.handleClose}
          text={i18n._('Got it', 'confirm reporting flow is done & close modal')}
        />
      );
    }

    // TODO how do we determine Block confirm modal classnames
    return (
      <div className={cx(className, { FlagBase: flow === Flow.REPORT })}>
        {!(flow === Flow.BLOCK) ? (
          <form className="standardForm" method="post">
            <Box marginRight={7} deprecatedPadding={2}>
              <Heading size="sm">{title}</Heading>
            </Box>
            <div
              className={cx('flagContent', {
                withBadRichDataLabel: aggregatedRichDataType,
                modalActionDescription: flow === Flow.BLOCK && status === Status.COMPLETED,
              })}
              style={flow === Flow.END || flagReason === 'IP' ? STYLES.contentContainer : null}
            >
              {this.renderContent()}
            </div>
            <div className="formFooter">
              <Box display="flex" alignItems="center">
                <Box flex="grow">{this.renderPolicyLink()}</Box>
                <Box deprecatedPadding={1}>{secondaryAction}</Box>
                <Box deprecatedPadding={1}>{primaryAction}</Box>
              </Box>
            </div>
          </form>
        ) : (
          <BlockAndUnfollow
            flagReason={flagReason}
            flagType={flagType}
            selectedReason={selectedReason}
            flow={flow}
            detailedReasons={detailedReasons}
            status={status}
            onClose={this.handleClose}
            users={users}
            onFlowStatusChange={this.handleFlowStatusChange}
            board={board}
          />
        )}
      </div>
    );
  }
}
